import React, { useState, useMemo } from 'react';
import { useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Table } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { useFilling } from '../context/FillingContext';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine, Label
} from 'recharts';
import { formatDateTime } from '../utils/formatDate';



// Lump inventory corrections (see backend/routes/fillings.js POST /admin-adjustment)
// are real Filling documents so the "filled" total stays accurate, but they
// aren't production work by anyone — exclude them from KPI/leaderboard
// gamification so an adjustment doesn't crown someone "Operador del Mes".
const isAdjustmentEntry = (e) =>
  typeof e.fillingID === 'string' && e.fillingID.startsWith('ADJ-');

const ProductionTable = ({ userName }) => {
  const { fillings, fetchFillings } = useFilling();

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filter, setFilter] = useState('Hoy');
  const [clientFilter, setClientFilter] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentNoteText, setCurrentNoteText] = useState('');
  const [currentFillingID, setCurrentFillingID] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [markers, setMarkers] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
const [editSaving, setEditSaving] = useState(false);
const [editError, setEditError] = useState('');

// Edit modal success/feedback
const [editSuccessMsg, setEditSuccessMsg] = useState('');

// Clients dropdown state
const [clientList, setClientList] = useState([]);
const [clientsLoading, setClientsLoading] = useState(false);
const [clientsError, setClientsError] = useState('');
const [showClientMenu, setShowClientMenu] = useState(false);
const [addingClient, setAddingClient] = useState(false);


const [editForm, setEditForm] = useState({
  fillingID: '',
  clients: '',
  ccCount: 0,
  pcCount: 0,
  totalCylinders: 0,
  startTime: '',   // datetime-local string
  endTime: '',     // datetime-local string
  totalTime: 0,    // minutes
});

const toLocalDTValue = (d) => {
  if (!d) return '';
  const date = new Date(d);
  // convert to local “YYYY-MM-DDTHH:mm”
  const tz = date.getTimezoneOffset() * 60000;
  return new Date(date - tz).toISOString().slice(0, 16);
};

const fromLocalDTValue = (s) => {
  // s is “YYYY-MM-DDTHH:mm” in local time
  return new Date(s);
};

const ensureClientList = async () => {
  if (clientList.length || clientsLoading) return;
  try {
    setClientsLoading(true);
    const res = await fetch('/api/clients', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const names = await res.json();
    if (Array.isArray(names)) setClientList(names);
  } catch (e) {
    console.error('clients fetch', e);
    setClientsError('No se pudo cargar la lista de clientes.');
  } finally {
    setClientsLoading(false);
  }
};

const handleAddClientFromModal = async (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return;
  try {
    setAddingClient(true);
    const res = await fetch('/api/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: trimmed })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // API returns { name } or a message; we normalize to `trimmed`
    setClientList(prev => Array.from(new Set([...prev, trimmed])).sort((a,b)=> a.localeCompare(b)));
    updateEditField('clients', trimmed);
    setShowClientMenu(false);
    setClientsError('');
  } catch (e) {
    console.error('add client', e);
    setClientsError('No se pudo agregar el cliente.');
  } finally {
    setAddingClient(false);
  }
};

const openEditModal = (entry) => {
  const cc = Number(entry.ccCount) || 0;
  const pc = Number(entry.pcCount) || 0;
  const total = Number(entry.totalCylinders) || (cc + pc);

  setEditForm({
    fillingID: entry.fillingID,
    clients: entry.clients || '',
    ccCount: cc,
    pcCount: pc,
    totalCylinders: total,
    startTime: toLocalDTValue(entry.startTime || entry.date),
    endTime: toLocalDTValue(entry.endTime || entry.date),
    totalTime: Number(entry.totalTime) || 0,
  });

  setEditError('');
  setEditSuccessMsg('');
  setShowClientMenu(false);
  setShowEditModal(true);
  ensureClientList(); // 🔹 fetch clients once
};

const updateEditField = (field, value) => {
  setEditForm((prev) => {
    const next = { ...prev, [field]: value };

    if (field === 'ccCount' || field === 'pcCount') {
      const cc = Number(field === 'ccCount' ? value : next.ccCount) || 0;
      const pc = Number(field === 'pcCount' ? value : next.pcCount) || 0;
      next.totalCylinders = cc + pc;
    }

    if (field === 'startTime' || field === 'endTime') {
      const start = next.startTime ? fromLocalDTValue(next.startTime) : null;
      const end   = next.endTime   ? fromLocalDTValue(next.endTime)   : null;
      if (start && end && end > start) {
        next.totalTime = Math.round((end - start) / 60000);
      }
    }
    return next;
  });
};


const saveEdit = async () => {
  setEditError('');
  setEditSuccessMsg('');
  try {
    setEditSaving(true);

    const body = { userName };
    if (editForm.clients !== undefined) body.clients = (editForm.clients || '').trim();

    const ccNum = Number(editForm.ccCount);
    if (!Number.isNaN(ccNum)) body.ccCount = ccNum;

    const pcNum = Number(editForm.pcCount);
    if (!Number.isNaN(pcNum)) body.pcCount = pcNum;

    if (editForm.startTime) {
      const s = new Date(editForm.startTime);
      if (isNaN(s.getTime())) return setEditError('Hora de inicio inválida.');
      body.startTime = s.toISOString();
    }
    if (editForm.endTime) {
      const e = new Date(editForm.endTime);
      if (isNaN(e.getTime())) return setEditError('Hora de fin inválida.');
      body.endTime = e.toISOString();
    }

    const res = await fetch(`/api/fillings/${encodeURIComponent(editForm.fillingID)}/edit`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try {
        const data = await res.json();
        if (data?.error) message += `: ${data.error}`;
        if (data?.detail) message += ` — ${data.detail}`;
      } catch {
        const txt = await res.text();
        message += `: ${txt.slice(0,200)}`;
      }
      throw new Error(message);
    }

    // Refresh table and show success inline
    await (typeof fetchFillings === 'function' ? fetchFillings() : Promise.resolve());
    setEditSuccessMsg('✅ Cambios guardados correctamente.');

    // Auto-close shortly after success
    setTimeout(() => {
      setEditSuccessMsg('');
      setShowEditModal(false);
    }, 1200);
  } catch (err) {
    console.error('saveEdit error:', err);
    setEditError(err.message || 'No se pudo guardar los cambios.');
  } finally {
    setEditSaving(false);
  }
};


  // Slideshow state
const [kpiIndex, setKpiIndex] = useState(0);

// Entries del mes y del año
const now = new Date();

const monthEntries = useMemo(() => {
  if (!Array.isArray(fillings)) return [];
  return fillings.filter(e => {
    if (isAdjustmentEntry(e)) return false;
    const d = new Date(e.endTime || e.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
}, [fillings]);

const yearEntries = useMemo(() => {
  if (!Array.isArray(fillings)) return [];
  return fillings.filter(e => {
    if (isAdjustmentEntry(e)) return false;
    const d = new Date(e.endTime || e.date);
    return d.getFullYear() === now.getFullYear();
  });
}, [fillings]);

// Agregados por operador (mes)
const aggByOperator = useMemo(() => {
  const map = new Map(); // user -> { fills, cyls, cc, pc, minutes, notes }
  monthEntries.forEach(e => {
    const u = e.completedBy || '—';
    if (!map.has(u)) map.set(u, { fills:0, cyls:0, cc:0, pc:0, minutes:0, notes:0 });
    const m = map.get(u);
    m.fills   += 1;
    m.cyls    += Number(e.totalCylinders) || 0;
    m.cc      += Number(e.ccCount) || 0;
    m.pc      += Number(e.pcCount) || 0;
    m.minutes += Number(e.totalTime) || 0;
    m.notes   += Array.isArray(e.notes) ? e.notes.length : 0;
  });
  return map;
}, [monthEntries]);

// 🥇 Más cilindros llenados (Mes)
const kpiTopCyls = useMemo(() => {
  if (aggByOperator.size === 0) return null;
  return [...aggByOperator.entries()]
    .map(([user, m]) => ({ user, value: m.cyls }))
    .sort((a,b) => b.value - a.value)[0];
}, [aggByOperator]);

// 📦 Más llenados completados (Mes)
const kpiTopFills = useMemo(() => {
  if (aggByOperator.size === 0) return null;
  return [...aggByOperator.entries()]
    .map(([user, m]) => ({ user, value: m.fills }))
    .sort((a,b) => b.value - a.value)[0];
}, [aggByOperator]);

// ⏱️ Menos minutos de llenados (Promedio ≥ 5 cilindros) – MENOR es mejor
const kpiMenosMinutos = useMemo(() => {
  const byUser = new Map(); // user -> { timeSum, count }
  monthEntries.forEach(e => {
    const qty = Number(e.totalCylinders) || 0;
    if (qty >= 5) {
      const u = e.completedBy || '—';
      if (!byUser.has(u)) byUser.set(u, { timeSum:0, count:0 });
      const obj = byUser.get(u);
      obj.timeSum += Number(e.totalTime) || 0;
      obj.count += 1;
    }
  });
  const arr = [...byUser.entries()]
    .filter(([_, v]) => v.count > 0)
    .map(([user, v]) => ({ user, avg: Math.round(v.timeSum / v.count) }))
    .sort((a,b) => a.avg - b.avg); // menor primero
  return arr[0] || null;
}, [monthEntries]);

// 🧪 Especialista en CC (Mes)
const kpiTopCC = useMemo(() => {
  if (aggByOperator.size === 0) return null;
  return [...aggByOperator.entries()]
    .map(([user, m]) => ({ user, value: m.cc }))
    .sort((a,b) => b.value - a.value)[0];
}, [aggByOperator]);

// 🧪 Especialista en PC (Mes)
const kpiTopPC = useMemo(() => {
  if (aggByOperator.size === 0) return null;
  return [...aggByOperator.entries()]
    .map(([user, m]) => ({ user, value: m.pc }))
    .sort((a,b) => b.value - a.value)[0];
}, [aggByOperator]);

// 📝 Campeón de notas (Mes)
const kpiTopNotes = useMemo(() => {
  if (aggByOperator.size === 0) return null;
  return [...aggByOperator.entries()]
    .map(([user, m]) => ({ user, value: m.notes }))
    .sort((a,b) => b.value - a.value)[0];
}, [aggByOperator]);

// 🏁 5 más rápido (Año) – totalCylinders === 5
const kpiFastestFiveYear = useMemo(() => {
  const five = yearEntries.filter(e => Number(e.totalCylinders) === 5 && typeof e.totalTime === 'number');
  if (five.length === 0) return null;
  const best = five.reduce((min, e) => (e.totalTime < min.totalTime ? e : min), five[0]);
  return { user: best.completedBy || '—', value: best.totalTime };
}, [yearEntries]);


  const filteredEntries = useMemo(() => {
  if (!Array.isArray(fillings)) return [];

  const { from, to } = getRangeFromFilter(filter);
  const fromMs = +from, toMs = +to;
  const q = (clientFilter || '').toLowerCase();

  return fillings.filter((entry) => {
    const endDate = new Date(entry.endTime || entry.date);
    if (isNaN(endDate)) return false;

    const inRange = endDate.getTime() >= fromMs && endDate.getTime() <= toMs;
    const matchesClient = (entry.clients || '').toLowerCase().includes(q);

    return inRange && matchesClient;
  });
}, [fillings, filter, clientFilter]);


  const sortedEntries = useMemo(() => {
    if (!sortConfig.key) return filteredEntries;

    return [...filteredEntries].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (['date', 'startTime', 'endTime'].includes(sortConfig.key)) {
        return sortConfig.direction === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return sortConfig.direction === 'asc'
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    });
  }, [filteredEntries, sortConfig]);

  const totalPages = Math.ceil(sortedEntries.length / itemsPerPage);
  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedEntries.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedEntries, currentPage]);

  const totalCC = filteredEntries.reduce((total, entry) => total + entry.ccCount, 0);
  const totalPC = filteredEntries.reduce((total, entry) => total + entry.pcCount, 0);
  const avgFillTime = filteredEntries.length > 0
    ? Math.round(filteredEntries.reduce((sum, e) => sum + e.totalTime, 0) / filteredEntries.length)
    : 0;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const openNoteModal = (fillingID) => {
    setCurrentFillingID(fillingID);
    setCurrentNoteText('');
    setShowNoteModal(true);
  };

  const addNote = async () => {
    if (!currentNoteText.trim()) return;

    const newNote = {
      id: uuidv4(),
      by: userName,
      text: currentNoteText,
      date: new Date().toISOString(),
    };

    try {
      await fetch(`/api/fillings/${currentFillingID}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newNote),
      });
      await fetchFillings(); // Refresh updated notes
    } catch (err) {
      console.error('❌ Error adding note:', err);
    }

    setCurrentNoteText('');
  };

  const getFillingNotes = () => {
    if (!Array.isArray(fillings)) return [];
    const entry = fillings.find(e => e.fillingID === currentFillingID);
    return Array.isArray(entry?.notes) ? entry.notes : [];
  };




// Build chart data for **current month only**
// Build chart data for current filter window
const chartData = useMemo(() => {
  if (!Array.isArray(filteredEntries)) return [];

  return [...filteredEntries]
    .sort((a, b) => new Date(a.endTime || a.date) - new Date(b.endTime || b.date))
    .map(e => {
      const end = new Date(e.endTime || e.date);

      const findReading = (arr, key) =>
        Array.isArray(arr) ? arr.find(r => r?.type === key) : null;

      const ambientReading   = findReading(e.readings, 'ambient_temperature');
      const dryerOutReading  = findReading(e.readings, 'Dryerout_temperature');

      return {
        timeMs: end.getTime(),                        // X axis (number)
        minutos: Number(e.totalTime) || 0,            // left Y axis
        ambient: ambientReading ? Number(ambientReading.value) : null,   // right Y axis
        dryerOut: dryerOutReading ? Number(dryerOutReading.value) : null,// right Y axis
        fillingID: e.fillingID,
      };
    });
}, [filteredEntries]);


  useEffect(() => {
  setCurrentPage(1);
}, [filter, clientFilter]);

const FastFiveLogo = ({ size = 72, color = '#12B8B2' }) => (
  <svg width={size} height={size} viewBox="0 0 128 128" role="img" aria-label="5 Más Rápidos">
    {/* Outer rings */}
    <circle cx="64" cy="64" r="58" fill={color} opacity="0.12" />
    <circle cx="64" cy="64" r="46" fill={color} />

    {/* Speed lines */}
    <rect x="18" y="50" width="22" height="5" rx="2.5" fill="white" opacity="0.85" />
    <rect x="26" y="62" width="26" height="5" rx="2.5" fill="white" opacity="0.75" />
    <rect x="34" y="74" width="18" height="5" rx="2.5" fill="white" opacity="0.65" />

    {/* Big "5" */}
    <text
      x="64"
      y="80"
      textAnchor="middle"
      fontFamily="system-ui, -apple-system, Segoe UI, Roboto, Arial"
      fontWeight="800"
      fontSize="58"
      fill="white"
    >
      5
    </text>

    {/* Lightning bolt - smaller & moved right */}
    <path
      d="M88 38 L74 66 H86 L76 90 L98 58 H86 Z"
      fill="white"
      opacity="0.9"
    />

    {/* Bottom label pill */}
    <rect x="34" y="92" width="60" height="18" rx="9" fill="white" opacity="0.95" />
    <text
      x="64"
      y="105"
      textAnchor="middle"
      fontFamily="system-ui, -apple-system, Segoe UI, Roboto, Arial"
      fontWeight="700"
      fontSize="10.5"
      fill={color}
    >
      MÁS RÁPIDOS
    </text>
  </svg>
);


const brand = '#12B8B2';

const Pill = ({ text, bg=brand }) => (
  <span
    className="badge"
    style={{ backgroundColor: bg, color:'#06222B', fontSize:'1.05rem', padding:'0.5rem 0.8rem', borderRadius:'0.8rem' }}
  >
    {text}
  </span>
);

const IconTrophy = ({ color=brand, size=28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M17 4V3a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v1H3v3a4 4 0 0 0 4 4h.09A6.003 6.003 0 0 0 11 15v3H8v2h8v-2h-3v-3a6.003 6.003 0 0 0 3.91-4H17a4 4 0 0 0 4-4V4h-4Zm-2 4a4 4 0 0 1-8 0V4h8v4Zm-10-1V6h2v2a2 2 0 0 1-2-2Zm16 0a2 2 0 0 1-2 2V6h2v1Z"/>
  </svg>
);

const IconStopwatch = ({ color=brand, size=28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M9 2h6v2H9V2Zm4 8V7h-2v5h4v-2h-2Zm7 4a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-2 0a6 6 0 1 0-12 0 6 6 0 0 0 12 0Z"/>
  </svg>
);

const IconCylinder = ({ color=brand, size=28 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="22" y="8" width="20" height="6" rx="2" fill={color}/>
    <rect x="20" y="14" width="24" height="40" rx="8" stroke={color} strokeWidth="3" fill="none"/>
  </svg>
);

const IconClipboard = ({ color=brand, size=28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M9 2h6a2 2 0 0 1 2 2v1h3v17H4V5h3V4a2 2 0 0 1 2-2Zm0 3V4h6v1H9Zm-1 5h8v2H8V10Zm0 4h8v2H8v-2Zm0 4h5v2H8v-2Z"/>
  </svg>
);

const slides = [
  {
    id: 'op-top-cyls',
    title: 'Operador del Mes',
    subtitle: 'Más cilindros llenados',
    badge: <Pill text="🥇 Producción" />,
    metric: kpiTopCyls ? `${kpiTopCyls.value} cilindros` : '—',
    user: kpiTopCyls?.user || '—',
    icon: <IconCylinder />
  },
  {
    id: 'op-top-fills',
    title: 'Máximo de Llenados',
    subtitle: 'Mayor número de llenados',
    badge: <Pill text="📦 Actividad" />,
    metric: kpiTopFills ? `${kpiTopFills.value} llenados` : '—',
    user: kpiTopFills?.user || '—',
    icon: <IconTrophy />
  },
  {
    id: 'op-fastest-avg',
    title: 'Menos minutos por llenado',
    subtitle: 'Promedio mas rapido de tiempo por llenado (≥ 5 cilindros)',
    badge: <Pill text="⏱️ Eficiencia" />,
    metric: kpiMenosMinutos ? `${kpiMenosMinutos.avg} min` : '—',
    user: kpiMenosMinutos?.user || '—',
    icon: <IconStopwatch />
  },
  {
    id: 'op-cc',
    title: 'Especialista en Cilindros de la Compania',
    subtitle: 'Más Cilindros de la Compania en el mes',
    badge: <Pill text="🧪 Cilindros de la Compania" />,
    metric: kpiTopCC ? `${kpiTopCC.value} CC` : '—',
    user: kpiTopCC?.user || '—',
    icon: <IconCylinder />
  },
  {
    id: 'op-pc',
    title: 'Especialista en Cilindros Personales',
    subtitle: 'Más Cilindros Personales en el mes',
    badge: <Pill text="🧪 Cilindros Personales" />,
    metric: kpiTopPC ? `${kpiTopPC.value} PC` : '—',
    user: kpiTopPC?.user || '—',
    icon: <IconCylinder />
  },
  {
    id: 'op-notes',
    title: 'Campeón de Notas',
    subtitle: 'Mejor documentación',
    badge: <Pill text="📝 Notas" />,
    metric: kpiTopNotes ? `${kpiTopNotes.value} notas` : '—',
    user: kpiTopNotes?.user || '—',
    icon: <IconClipboard />
  },
  {
    id: 'fastest-five-year',
    title: '5 más rápido (Año)',
    subtitle: `Año ${now.getFullYear()}`,
    badge: <Pill text="🏁 Récord" />,
    metric: kpiFastestFiveYear ? `${kpiFastestFiveYear.value} min` : '—',
    user: kpiFastestFiveYear?.user || '—',
    icon: <IconStopwatch />
  },
];



// Fastest filling for EXACTLY 5 tanks this month
const fastestFive = useMemo(() => {
  const five = monthEntries.filter(e =>
    Number(e.totalCylinders) === 5 && typeof e.totalTime === 'number'
  );
  if (five.length === 0) return null;
  return five.reduce((min, e) => (e.totalTime < min.totalTime ? e : min), five[0]);
}, [monthEntries]);

// Leaderboard: users by number of fillings this month (desc)
const leaderboard = useMemo(() => {
  const map = new Map();
  monthEntries.forEach(e => {
    const user = e.completedBy || userName || '—';
    const cyls = Number(e.totalCylinders) || 0;
    map.set(user, (map.get(user) || 0) + cyls);
  });
  return [...map.entries()]
    .map(([user, count]) => ({ user, count }))
    .sort((a, b) => b.count - a.count);
}, [monthEntries, userName]);


const fmtTime = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit'
});

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const p = payload[0].payload;
  const fillId = p.fillingID || p.fillId || p.id || p._id || '—';

  const labelText =
    typeof label === 'number'
      ? fmtTime.format(new Date(label))
      : String(label ?? '');

  const hasAmbient = p.ambient != null && !Number.isNaN(p.ambient);
  const hasDryer   = p.dryerOut != null && !Number.isNaN(p.dryerOut);

  return (
    <div style={{ background: 'var(--mm-panel)', border: '1px solid var(--mm-line)', color: 'var(--mm-text)', padding: 8, borderRadius: 6 }}>
      <div><strong>Hora:</strong> {labelText}</div>
      <div><strong>ID:</strong> {fillId}</div>
      <div style={{ color: '#12B8B2' }}><strong>Duración:</strong> {p.minutos} min</div>
      {hasAmbient ? (
        <div style={{ color: '#F5B700' }}><strong>Temp. Ambiente:</strong> {p.ambient}°C</div>
      ) : (
        <div className="text-muted"><em>Sin temp. ambiente</em></div>
      )}
      {hasDryer ? (
        <div style={{ color: '#F87171' }}><strong>Temp. Secador:</strong> {p.dryerOut}°C</div>
      ) : (
        <div className="text-muted"><em>Sin temp. secador</em></div>
      )}
    </div>
  );
};

// chart component (top-level, no export)
const FillVsTempChart = ({ chartData, events = [], xDomainMs }) => {
  const hasData = Array.isArray(chartData) && chartData.length > 0;
  const yDomain = hasData ? [0, 'dataMax + 10'] : [0, 100]; // fallback if empty

  // dynamic top margin so labels don't clip when lanes increase
const maxLane = events?.length ? Math.max(...events.map(e => e.lane || 0)) : 0;
const marginTop = 48 + maxLane * 16; // base + per-lane padding

  return (
    <ResponsiveContainer width="100%" height="100%">
       <LineChart
    data={chartData}
   margin={{ top: marginTop, right: 56, left: 8, bottom: 24 }}   // ↑ add headroom
  >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,242,245,0.10)" />

        <XAxis
          dataKey="timeMs"
          type="number"
          domain={xDomainMs || ['dataMin', 'dataMax']}
          stroke="#9DB3C2"
          tick={{ fill: '#9DB3C2' }}
          tickFormatter={(ms) =>
            new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        />

        <YAxis
          yAxisId="min"
          orientation="left"
          domain={yDomain}
          stroke="#9DB3C2"
          tick={{ fill: '#9DB3C2' }}
          label={{ value: 'Min', angle: -90, position: 'insideLeft', fill: '#9DB3C2' }}
          tickFormatter={(v) => `${v} min`}
          width={48}
        />
        <YAxis
          yAxisId="mirror"
          orientation="right"
          domain={yDomain}
          stroke="#9DB3C2"
          tick={{ fill: '#9DB3C2' }}
          ticks={[22, 25, 30, 35]}
          tickFormatter={(v) => `${v}°C`}
          axisLine={false}
          tickLine={false}
          width={56}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
          labelFormatter={(ms) =>
            new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        />
        <Legend />
{/* Minutes (left axis) */}
<Line
  yAxisId="min"
  type="monotone"
  dataKey="minutos"
  stroke="#009688"
  strokeWidth={3}
  dot={{ r: 3 }}
  name="Minutos"
  connectNulls
  isAnimationActive={false}
/>

{/* Ambient temperature (right axis) */}
<Line
  yAxisId="mirror"
  type="monotone"
  dataKey="ambient"
  stroke="#F5B700"
  strokeWidth={3}
  dot={{ r: 3 }}
  name="Temp. Ambiente (°C)"
  connectNulls
  isAnimationActive={false}
/>

{/* Dryer outlet temperature (right axis) */}
<Line
  yAxisId="mirror"
  type="monotone"
  dataKey="dryerOut"
  stroke="#F87171"
  strokeWidth={3}
  dot={{ r: 3 }}
  name="Temp. Secador (°C)"
  connectNulls
  isAnimationActive={false}
/>

        {/* 👉 with explicit axis ids */}
       {events.map((evt) => (
  <ReferenceLine
    key={evt.id}
    xAxisId={0}
    yAxisId="min"       // important so it spans your existing left axis
    x={evt.timeMs}
    stroke={evt.color}
    strokeDasharray="6 6"
    strokeWidth={2}
    ifOverflow="extendDomain"
  >
    <Label content={(props) => <EventLabelOutside {...props} evt={evt} />} />
  </ReferenceLine>
))}


      </LineChart>
    </ResponsiveContainer>
  );
};

// --- Marker meta (labels/colors used by the chart)
const MARKER_META = {
  apagone:  { label: 'Apagón eléctrico',      color: '#F87171' },
  arranque: { label: 'Arranque de bomba LOX', color: '#4ADE80' },
  parada:   { label: 'Bomba se detuvo',       color: '#F87171' },
  tamiz:    { label: 'Cambio de tamiz',       color: '#F5B700' },
};

// Match your dropdown values → time window
function getRangeFromFilter(filter) {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const endToday   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  if (filter === 'Hoy') return { from: startToday, to: endToday };

  if (filter === 'Ayer') {
    const s = new Date(startToday); s.setDate(s.getDate() - 1);
    const e = new Date(endToday);   e.setDate(e.getDate() - 1);
    return { from: s, to: e };
  }

  if (filter === 'Ultimo 7 Dias' || filter === 'Ultimo 7 Días') {
    const s = new Date(startToday); s.setDate(s.getDate() - 6);
    return { from: s, to: endToday };
  }

  if (filter === 'Este mes' || filter === 'Este Mes') {
    const s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return { from: s, to: endToday }; // or end-of-month if you prefer
  }

  return { from: startToday, to: endToday };
}



// Make the marker's X value match your chart's X labels (you use HH:mm)
const toXAxisLabel = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// pixels to shift label horizontally per lane (center, right, left, …)

function offsetForLane(lane = 0) {
  const pattern = [0, 56, -56, 112, -112, 168, -168]; // tweak spread if needed
  return pattern[lane % pattern.length];
}



// Decorate API rows into what your chart expects
function decorateMarkers(rows) {
  return rows
    .map(r => {
      const id   = r.id ?? r._id ?? r._doc?._id;                        // tolerate raw mongo
      const type = r.type ?? r.marker_type ?? r.event_type ?? 'evento';  // tolerate variants
      const t    = r.time ?? r.occurred_at ?? r.when ?? r.timestamp;     // ISO or Date string

      const ms = Number.isFinite(t) ? Number(t) : new Date(t).getTime(); // accept epoch or ISO
      const meta = MARKER_META[type] || { label: String(type), color: '#999' };

      return {
        id,
        label: meta.label,
        color: meta.color,
        timeISO: (typeof t === 'string') ? t : (Number.isFinite(ms) ? new Date(ms).toISOString() : null),
        timeMs: ms,
        notes: r.notes,
        operator: r.operator || null,
      };
    })
    .filter(m => Number.isFinite(m.timeMs)); // drop invalid rows to avoid ReferenceLine NaN
}


const { from: xFrom, to: xTo } = getRangeFromFilter(filter);


// Modal UI state (just for the form)
const [eventOpen, setEventOpen] = useState(false);
const [saving, setSaving] = useState(false);
const [eventForm, setEventForm] = useState({
  type: 'apagone',
  time: chartData?.[chartData.length - 1]?.timeISO || '', // if you have an ISO on your last point
  notes: '',
  operator: '', // optional
});
const [eventErrors, setEventErrors] = useState({});

// Fetch markers whenever the filter changes
useEffect(() => {
  let abort = false;
  (async () => {
    try {
      const { from, to } = getRangeFromFilter(filter);
      const qs = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
        limit: '500',
      }).toString();

      const res = await fetch(`/api/production-timeline-markers?${qs}`);
      if (!res.ok) throw new Error('Failed to load markers');
      const rows = await res.json();
      if (!abort) setMarkers(layoutEventLanes(decorateMarkers(rows)));
    } catch (e) {
      console.error(e);
      if (!abort) setMarkers([]);
    }
  })();
  return () => { abort = true; };
}, [filter]);


// Save event → POST to API → refresh markers
async function handleSaveEvent() {
  const errs = {};
  if (!eventForm.type) errs.type = 'Requerido';
  if (!eventForm.time) errs.time = 'Requerido';
  if (!eventForm.notes?.trim()) errs.notes = 'Requerido';
  setEventErrors(errs);
  if (Object.keys(errs).length) return;

  try {
    setSaving(true);
    // Convert local datetime-local to ISO for backend
    const iso = new Date(eventForm.time).toISOString();

    const res = await fetch('/api/production-timeline-markers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: eventForm.type,
        time: iso,
        notes: eventForm.notes.trim(),
        operator: eventForm.operator || undefined,
        userName,
      }),
    });
    if (!res.ok) throw new Error('Failed to save event');

    // Close modal
    setEventOpen(false);
    setEventForm(f => ({ ...f, notes: '' }));

    // Refresh markers for the current filter
   // after saving:
const { from, to } = getRangeFromFilter(filter);
const qs = new URLSearchParams({
  from: from.toISOString(),
  to: to.toISOString(),
  limit: '500',
}).toString();
const fresh = await fetch(`/api/production-timeline-markers?${qs}`).then(r => r.json());
setMarkers(layoutEventLanes(decorateMarkers(fresh)));

  } catch (e) {
    console.error(e);
    alert('No se pudo guardar el evento.');
  } finally {
    setSaving(false);
  }
}

// Multi-line wrap (2 lines by default)
function wrapLabel(text, maxChars = 22, maxLines = 2) {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const candidate = line ? `${line} ${w}` : w;
    if (candidate.length <= maxChars) line = candidate;
    else {
      if (line) lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) {
        const rest = [line, ...words.slice(i + 1)].join(' ');
        lines.push(rest.length > maxChars ? rest.slice(0, maxChars - 1) + '…' : rest);
        return lines;
      }
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

// Lane layout (you likely already have this)
function layoutEventLanes(events, { lanes = 3, minGapMs = 15 * 60 * 1000 } = {}) {
  const last = Array(lanes).fill(-Infinity);
  return [...events]
    .sort((a, b) => a.timeMs - b.timeMs)
    .map(evt => {
      let lane = 0;
      for (; lane < lanes; lane++) {
        if (evt.timeMs - last[lane] > minGapMs) { last[lane] = evt.timeMs; break; }
      }
      if (lane === lanes) lane = lanes - 1;
      return { ...evt, lane };
    });
}

// 🔼 Renders label ABOVE the plot area, centered on the line, staggered by lane
const EventLabelOutside = ({ viewBox, evt }) => {
  if (!viewBox || !evt) return null;

  const lines = wrapLabel(evt.label, 22, 2); // full name, wrap to 2 lines
  const x0 = viewBox.x;                      // marker x
  const yTop = viewBox.y;                    // top of plot area

  const lineHeight = 14;
  const maxLines = 2;
  const laneGap = 6;
  const basePad = 6;
  const lane = evt.lane || 0;
  const laneStep = lineHeight * maxLines + laneGap;

  // label position (outside, above)
  const dx = offsetForLane(lane);
  const xLabel = x0 + dx;
  const yLabel = yTop - basePad - lane * laneStep;

  // connect from marker top to bottom of the label block
  const yTextBottom = yLabel + (Math.min(lines.length, maxLines) - 1) * lineHeight;

  return (
    <g style={{ pointerEvents: 'none' }}>
      {/* leader line */}
      <line
        x1={x0}
        y1={yTop - 1}
        x2={xLabel}
        y2={yTextBottom + 2}
        stroke={evt.color}
        strokeWidth={1}
        opacity="0.9"
      />
      {/* (optional) small dot at label side */}
      <circle cx={xLabel} cy={yTextBottom + 2} r={2} fill={evt.color} opacity="0.9" />

      {/* the wrapped text, centered above the line */}
      <g transform={`translate(${xLabel},${yLabel})`}>
        <text fontSize={12} fill={evt.color} textAnchor="middle">
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>{line}</tspan>
          ))}
        </text>
      </g>
    </g>
  );
};


const EventLabel = ({ viewBox, evt }) => {
  if (!viewBox || !evt) return null;
  const lines = wrapLabel(evt.label, 18, 2);
  const x = viewBox.x;                                 // center on the line
  const y = viewBox.y + 8 + (evt.lane || 0) * 16;      // top + lane stacking
  return (
    <g transform={`translate(${x},${y})`}>
      <text fontSize={12} fill={evt.color} textAnchor="middle">
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : 14}>{line}</tspan>
        ))}
      </text>
    </g>
  );
};


  return (
    <div className="table-responsive w-100">
      <div className="card shadow p-4 bg-white rounded" style={{ fontSize: '0.85rem' }}>
        <h2 className="mb-4 text-center">Tabla de Producción</h2>

        {/* === Mini Analytics Card: Tiempo vs Temperatura === */}
<div className="card shadow-sm mb-4">
  <div className="card-body">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h5 className="card-title mb-0">Tiempo de Llenado vs Temperatura Ambiente</h5>
       <button className="btn btn-outline-primary btn-sm" onClick={() => setEventOpen(true)}>
    Agregar evento
  </button>
      <small className="text-muted">
  Verde: Minutos · <span style={{ color: '#F5B700' }}>Naranja: Temp. Ambiente</span> · <span style={{ color: '#F87171' }}>Rojo: Temp. Secador</span>
</small>


    </div>

    <div style={{ width: '100%', height: 360 }}>
      <FillVsTempChart chartData={chartData} events={markers} xDomainMs={[+xFrom, +xTo]} />
    {eventOpen && (
  <div
    role="dialog"
    aria-modal="true"
    onClick={() => setEventOpen(false)}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ width: 420, background: 'var(--mm-panel)', border: '1px solid var(--mm-line)', color: 'var(--mm-text)', borderRadius: 12, padding: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.4)' }}
    >
      <h5 className="mb-3">Agregar evento</h5>

      <div className="form-group mb-2">
        <label>Evento</label>
        <select
          className="form-control"
          value={eventForm.type}
          onChange={(e) => setEventForm(f => ({ ...f, type: e.target.value }))}
        >
          <option value="apagone">Apagón eléctrico</option>
          <option value="arranque">Arranque de bomba LOX</option>
          <option value="parada">Bomba se detuvo</option>
          <option value="tamiz">Cambio de tamiz</option>
        </select>
        {eventErrors.type && <small className="text-danger">{eventErrors.type}</small>}
      </div>

      <div className="form-group mb-2">
        <label>Fecha y hora</label>
        <input
          type="datetime-local"
          className="form-control"
          value={eventForm.time}
          onChange={(e) => setEventForm(f => ({ ...f, time: e.target.value }))}
        />
        {eventErrors.time && <small className="text-danger">{eventErrors.time}</small>}
      </div>

      <div className="form-group mb-2">
        <label>Notas (requerido)</label>
        <textarea
          className="form-control"
          rows={3}
          value={eventForm.notes}
          onChange={(e) => setEventForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Detalle del evento…"
        />
        {eventErrors.notes && <small className="text-danger">{eventErrors.notes}</small>}
      </div>

      <div className="form-group mb-3">
        <label>Operador (opcional)</label>
        <input
          className="form-control"
          value={eventForm.operator}
          onChange={(e) => setEventForm(f => ({ ...f, operator: e.target.value }))}
          placeholder="Nombre del operador"
        />
      </div>

      <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
        <button className="btn btn-light" onClick={() => setEventOpen(false)} disabled={saving}>
          Cancelar
        </button>
        <button className="btn btn-primary" onClick={handleSaveEvent} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar evento'}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  </div>
</div>


        {/* Mini dashboard cards */}
<div className="row g-3 mb-4">

  
  {/* Destacados del Mes */}
<div className="col-md-6">
  <div className="card shadow-sm h-100">
    <div className="card-body">
      {/* Header with icon & badge */}
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="card-title mb-1">{slides[kpiIndex].title}</h5>
          <div className="text-muted small">{slides[kpiIndex].subtitle}</div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          {slides[kpiIndex].badge}
          <div className="ms-2">{slides[kpiIndex].icon}</div>
        </div>
      </div>

      {/* Main metric */}
      <div className="display-6 fw-bold">
        {slides[kpiIndex].metric}
      </div>

      {/* User badge */}
      <div className="mt-2">
        <span
          className="badge"
          style={{
            backgroundColor: brand,
            color: '#06222B',
            fontSize: '1.25rem',
            padding: '0.6rem 0.9rem',
            borderRadius: '0.75rem',
          }}
        >
          {slides[kpiIndex].user}
        </span>
      </div>

      {/* Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setKpiIndex(i => (i - 1 + slides.length) % slides.length)}
        >
          ‹ Anterior
        </button>
        <div>
          {slides.map((_, i) => (
            <span
              key={i}
              style={{
                display:'inline-block',
                width:8, height:8, borderRadius:'50%',
                margin:'0 3px',
                backgroundColor: i === kpiIndex ? brand : 'rgba(157,179,194,0.3)'
              }}
            />
          ))}
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setKpiIndex(i => (i + 1) % slides.length)}
        >
          Siguiente ›
        </button>
      </div>
    </div>
  </div>
</div>

  {/* Leaderboard (Mes) */}
  <div className="col-md-6">
    <div className="card shadow-sm h-100">
      <div className="card-body">
        <h5 className="card-title mb-3">Leaderboard (Mes)</h5>
        {leaderboard.length === 0 ? (
          <div className="text-muted">Sin registros</div>
        ) : (
          <ol className="m-0 list-unstyled">
  {leaderboard.slice(0, 5).map(row => (
    <li
      key={row.user}
      className="d-flex justify-content-between align-items-center py-2 border-bottom"
    >
      <span className="fw-semibold">{row.user}</span>
      <span
        className="badge bg-primary"
        style={{ fontSize: '1.1rem', color: "#06222B", padding: '0.5rem 0.75rem' }}
      >
        {row.count}
      </span>
    </li>
  ))}
</ol>

        )}
      </div>
    </div>
  </div>
</div>


        <div className="row justify-content-center mb-3">
          <div className="col-md-3 text-center">
            <label className="font-weight-bold">Filtrar por fecha:</label>
            <select className="form-control" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="Hoy">Hoy</option>
              <option value="Ayer">Ayer</option>  
              <option value="Ultimo 7 Dias">Ultimo 7 Días</option>
              <option value="Este mes">Este Mes</option>
            </select>
          </div>
          <div className="col-md-4 text-center">
            <label className="font-weight-bold">Buscar cliente:</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ej: Clínica Central"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="row text-center mb-4">
          <div className="col-md-3"><strong>Entradas:</strong> {filteredEntries.length}</div>
          <div className="col-md-3"><strong>CC:</strong> {totalCC}</div>
          <div className="col-md-3"><strong>PC:</strong> {totalPC}</div>
          <div className="col-md-3"><strong>Prom. Tiempo:</strong> {avgFillTime} min</div>
        </div>

        <table className="table table-sm table-striped table-bordered table-hover align-middle text-center">
          <thead className="thead-dark">
            <tr>
              <th onClick={() => handleSort('fillingID')}>ID</th>
              <th onClick={() => handleSort('date')}>Fecha</th>
              <th onClick={() => handleSort('totalCylinders')}>Total</th>
              <th onClick={() => handleSort('ccCount')}>CC</th>
              <th onClick={() => handleSort('pcCount')}>PC</th>
              <th onClick={() => handleSort('clients')}>Cliente</th>
              <th onClick={() => handleSort('completedBy')}>Por</th>
              <th onClick={() => handleSort('startTime')}>Inicio</th>
              <th onClick={() => handleSort('endTime')}>Fin</th>
              <th onClick={() => handleSort('totalTime')}>Min</th>
              <th>Notas</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEntries.map(entry => (
              <tr key={entry.fillingID}>
                <td>{entry.fillingID}</td>
                <td>{entry.date}</td>
                <td>{entry.totalCylinders}</td>
                <td>{entry.ccCount}</td>
                <td>{entry.pcCount}</td>
                <td>{entry.clients}</td>
                <td>{entry.completedBy || userName}</td>
                <td>{new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                <td>{new Date(entry.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                <td>{entry.totalTime}</td>
                <td className="text-nowrap">
                  {/* Clipboard (notes) — always bordered, dim if no notes */}
                  <button
                    className={`btn btn-sm btn-outline-secondary me-2 d-inline-flex align-items-center justify-content-center ${entry.notes?.length ? '' : 'opacity-50'}`}
                    style={{ width: 34, height: 34 }}
                    onClick={() => openNoteModal(entry.fillingID)}
                    title={entry.notes?.length ? 'Ver notas' : 'Sin notas'}
                  >
                    <i className="bi bi-clipboard"></i>
                  </button>

                  {/* Pencil (edit) — same look & size */}
                  <button
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() => openEditModal(entry)}
                    title="Editar llenado"
                  >
                    <i className="bi bi-pencil-square"></i>
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex justify-content-center mt-3">
          <Button variant="secondary" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="me-2">Anterior</Button>
          <span className="align-self-center px-2">Página {currentPage} de {totalPages}</span>
          <Button variant="secondary" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="ms-2">Próxima</Button>
        </div>
      </div>

      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Notas del llenado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Fecha</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                {getFillingNotes().map((note, index) => (
                  <tr key={note.id || index}>
                    <td>{note.by}</td>
                    <td>{formatDateTime(note.date)}</td>
                    <td>{note.text}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <textarea
            className="form-control"
            rows="3"
            placeholder="Agregar nueva nota..."
            value={currentNoteText}
            onChange={(e) => setCurrentNoteText(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)}>Cerrar</Button>
          <Button variant="primary" onClick={addNote}>Agregar Nota</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
  <Modal.Header closeButton>
    <Modal.Title>Editar Llenado {editForm.fillingID ? `(${editForm.fillingID})` : ''}</Modal.Title>
  </Modal.Header>

  <Modal.Body>
   {editError && <div className="alert alert-danger py-2">{editError}</div>}
  {editSuccessMsg && <div className="alert alert-success py-2 mb-2">{editSuccessMsg}</div>}


    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">CC</label>
        <input
          type="number"
          min="0"
          className="form-control"
          value={editForm.ccCount}
          onChange={(e) => updateEditField('ccCount', e.target.value)}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">PC</label>
        <input
          type="number"
          min="0"
          className="form-control"
          value={editForm.pcCount}
          onChange={(e) => updateEditField('pcCount', e.target.value)}
        />
      </div>

      <div className="col-12 position-relative">
  <label className="form-label">Cliente</label>
  <input
    type="text"
    className="form-control"
    value={editForm.clients}
    placeholder="Buscar o agregar cliente…"
    onChange={(e) => { updateEditField('clients', e.target.value); setShowClientMenu(true); }}
    onFocus={() => setShowClientMenu(true)}
    onBlur={() => setTimeout(() => setShowClientMenu(false), 150)} // allow click on menu
    autoComplete="off"
  />

  {showClientMenu && (
    <div className="dropdown-menu show w-100 mt-1 shadow-sm" style={{ maxHeight: 240, overflowY: 'auto' }}>
      {clientsLoading ? (
        <div className="dropdown-item text-muted">Cargando…</div>
      ) : (
        <>
          {clientList
            .filter(n => n.toLowerCase().includes((editForm.clients || '').toLowerCase()))
            .slice(0, 50)
            .map((name, i) => (
              <button
                type="button"
                key={i}
                className="dropdown-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { updateEditField('clients', name); setShowClientMenu(false); }}
              >
                {name}
              </button>
            ))}

          {/* Add-new option if not present */}
          {editForm.clients &&
            !clientList.map(n => n.toLowerCase()).includes(editForm.clients.toLowerCase().trim()) &&
            !clientsLoading && (
              <>
                <div className="dropdown-divider" />
                <button
                  type="button"
                  className="dropdown-item text-primary d-flex align-items-center"
                  style={{ gap: '8px' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleAddClientFromModal(editForm.clients)}
                  disabled={addingClient}
                >
                  {addingClient ? 'Guardando…' : <>Agregar "<strong>{editForm.clients.trim()}</strong>" como nuevo cliente</>}
                </button>
              </>
            )}
        </>
      )}
    </div>
  )}

  {clientsError && <small className="text-danger">{clientsError}</small>}
</div>


      <div className="col-md-6">
        <label className="form-label">Inicio</label>
        <input
          type="datetime-local"
          className="form-control"
          value={editForm.startTime}
          onChange={(e) => updateEditField('startTime', e.target.value)}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Fin</label>
        <input
          type="datetime-local"
          className="form-control"
          value={editForm.endTime}
          onChange={(e) => updateEditField('endTime', e.target.value)}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Total de Cilindros</label>
        <input className="form-control" value={editForm.totalCylinders} readOnly />
      </div>

      <div className="col-md-6">
        <label className="form-label">Duración (min)</label>
        <input className="form-control" value={editForm.totalTime} readOnly />
      </div>
    </div>
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editSaving}>
      Cancelar
    </Button>
    <Button variant="primary" onClick={() => saveEdit()} disabled={editSaving}>
  {editSaving ? 'Guardando…' : 'Guardar Cambios'}
</Button>

  </Modal.Footer>
</Modal>

    </div>

    
  );
};

export default ProductionTable;
